import React, { useState, useEffect, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CircularProgress, Divider, Link, Typography } from '@mui/material'
import i18n from 'i18next'
import clsx from 'clsx'

import { CourseApi } from '~api/instances'
import { CourseRegistrationFormPrefills, RegisterCourseRequest, useCourseNameOptions } from '~api/models'
import GridContainer from '~shared/components/MuiGrid/GridContainer'
import { FormContext } from '~shared/components/reactHookForm/FormContext'
import Row from '~shared/components/MuiGrid/Row'
import Col from '~shared/components/MuiGrid/Col'
import { useGlobalStyles } from '~shared/styles/globalStyles'
import CircularProgressButton from '~shared/components/ProgressButton/CircularProgressButton'
import { useAddToast } from '~utils/useAddToast'
import ScreenContainerMain from '~shared/components/ScreenContainerMain'
import FormTextFieldRedesign from '~shared/components/reactHookForm/input/FormTextFieldRedesign'
import { useRegStyles } from './RegistrationCourse.styles'
import { CourseRegistrationForm, RegTrainingSchema } from './RegistrationCourse.schema'
import { AsyncModel } from '~shared/models/AsyncModel'
import FormCheckbox from '~shared/components/reactHookForm/input/FormCheckbox'
import { useAutocompleteName } from '~utils/autocompleteType'
import agbPdfDe from '~assets/files/AGBs_Service_Schulungen_DE.pdf'
import agbPdfEn from '~assets/files/AGBs_Service_Schulungen_EN.pdf'
import { useSelector } from 'react-redux'
import { getUser } from '~store/account/selectors'
import FormAutocompleteRedesign from '~shared/components/reactHookForm/input/FormAutocomplete/FormAutocompleteRedesign'

const RegistrationCourse: React.FC = () => {
  const gs = useGlobalStyles()
  const s = useRegStyles()
  const resolver = yupResolver<CourseRegistrationForm>(RegTrainingSchema)
  const methods = useForm<CourseRegistrationForm>({ resolver })
  const { handleSubmit, reset } = methods
  const addToast = useAddToast()
  const { t } = useTranslation()
  const acFieldName = useAutocompleteName<CourseRegistrationForm>()
  const user = useSelector(getUser)
  const courseOptions = useCourseNameOptions()

  const [linkCourse, setLinkCourse] = useState<string>(t('link:courseOverview'))

  const [coursePrefills, setCoursePrefills] = useState<AsyncModel<CourseRegistrationFormPrefills>>({})
  const fetchRegisterCoursePrefills = useCallback(() => {
    setCoursePrefills({ ...coursePrefills, inProgress: true })
    CourseApi.getRegisterCoursePrefills()
      .then((data) => {
        setCoursePrefills({ ...coursePrefills, payload: data, error: undefined, inProgress: false })
      })
      .catch((data) => {
        setCoursePrefills({ ...coursePrefills, error: data, inProgress: false })
      })
  }, [coursePrefills])

  useEffect(() => {
    fetchRegisterCoursePrefills()
    // eslint-disable-next-line -- on mount
  }, [])

  const [submitInProgress, setSubmitInProgress] = useState(false)
  const onSubmit = (formData: CourseRegistrationForm) => {
    setSubmitInProgress(true)
    const registerCourseRequestProps: RegisterCourseRequest = {
      courseRegistrationForm: {
        courseName: formData.courseName,
        email: formData.email,
        phone: formData.phone,
        attendeeName1: formData.attendeeName1,
        attendeeName2: formData.attendeeName2,
        attendeeName3: formData.attendeeName3,
        attendeeName4: formData.attendeeName4,
      },
    }

    CourseApi.registerCourse(registerCourseRequestProps)
      .then(() => {
        addToast({ severity: 'success', content: t('common:submitRegistrationCourseSuccess') })
        reset() // reset form
      })
      .catch((error) => {
        addToast({
          severity: 'error',
          content: error.statusText
            ? `${t('common:submitRegistrationCourseFailure')}: ${error.statusText}`
            : t('error:unknownErrorHasOccurred'),
        })
      })
      .finally(() => {
        setSubmitInProgress(false)
      })
  }

  return <ScreenContainerMain>
    <Typography variant='h2' component='h1' className={gs.marginBottomMedium}>
      {t('common:navHeaderRegistrationCourse')}
    </Typography>

    <FormContext.Provider value={{ schema: RegTrainingSchema }}>
      <FormProvider {...methods}>
        {/* {isDebugEnv && <RhfDebugHelper />} */}
        <form
          noValidate // disable html validation in favor of yup
          onSubmit={handleSubmit(onSubmit)}
        >
          <GridContainer gridSpacing={5} defaultColWidth={6} maxWidth='Md' elevation={1} roundCornersTop roundCornersBottom>
            <Row className={gs.gridTopMarginFix}>
              <Col xs={12} className={gs.paddingTop0}>
                <Typography variant='h3' component='h2'>
                  {t('common:registrationCourseHeader')}
                </Typography>
                <Typography className={clsx(gs.marginBottomTiny, gs.italic)}>
                  {t('common:registrationCourseSubHeader')}
                </Typography>
                <Divider
                  className={gs.marginBottomTiny}
                  variant='fullWidth'
                  style={{ maxWidth: '125px', backgroundColor: '#000' }}
                />
                <Typography variant='body1' className={clsx(gs.marginBottomSmall, s.description)}>
                  {t('common:registrationCourseDescription')}
                </Typography>
              </Col>
            </Row>
            {
              coursePrefills.inProgress
              && <CircularProgress color='primary' />
            }
            {
              coursePrefills.payload
              && <>
                <Row className={s.zIndex}>
                  <Col xs={12}>
                    <FormAutocompleteRedesign
                      label={t('common:registrationCourseInputLabelCourseName')}
                      name={acFieldName('courseName')}
                      options={courseOptions}
                      fullWidth
                      onChange={(value) => { setLinkCourse(value != null ? value.value.toString() : t('link:courseOverview')) }}
                    />
                    <Link
                      // if a course is selected, link will direct you to this course-page, else link will direct you to course overview
                      href={linkCourse}
                      target='_blank'
                      title='Link To Course'
                    >
                      {t('course:linkToCourse')}
                    </Link>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormTextFieldRedesign
                      label={t('common:registrationCourseInputLabelCompanyName')}
                      name={acFieldName('companyName')}
                      defaultValue={coursePrefills.payload.companyName}
                      fullWidth
                      disabled
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormTextFieldRedesign
                      label={t('common:registrationCourseInputLabelStreetNr')}
                      name={acFieldName('streetAndNumber')}
                      defaultValue={coursePrefills.payload.streetAndNumber}
                      disabled
                    />
                  </Col>
                  <Col>
                    <FormTextFieldRedesign
                      label={t('common:registrationCourseInputLabelZipCity')}
                      name={acFieldName('zip')}
                      defaultValue={`${coursePrefills.payload.zip} ${coursePrefills.payload.city}`}
                      disabled
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormTextFieldRedesign
                      label={t('common:registrationCourseInputLabelPhoneNr')}
                      defaultValue={`${coursePrefills.payload.phone}`}
                      name={acFieldName('phone')}
                    />
                  </Col>
                  <Col>
                    <FormTextFieldRedesign
                      label={t('common:registrationCourseInputLabelEmail')}
                      name={acFieldName('email')}
                      defaultValue={`${user?.email}`}
                    />
                  </Col>
                </Row>
                {[...Array(4)].map((x, i) => {
                  return (<Row key={i}>
                    <Col xs={12}>
                      <FormTextFieldRedesign
                        label={t('common:registrationCourseInputLabelAttendeesName', { number: i + 1 })}
                        name={`attendeeName${i + 1}`}
                        fullWidth
                      />
                    </Col>
                  </Row>)
                })}
                <Row>
                  <Col xs={12}>
                    <p>{t('common:registrationCourseCoronaInfo')}</p>
                    <FormCheckbox
                      name={acFieldName('agbAccepted')}
                      text={<Trans i18nKey='common:registrationCourseInputLabelAgbAccepted'>
                        1<a href={i18n.language === 'de-DE' ? agbPdfDe : agbPdfEn} target='_blank' rel='noreferrer'>2</a>3
                      </Trans>}
                      color='primary'
                      dontAlignTextTop
                    />
                  </Col>
                </Row>
                <Row className={clsx(gs.gridTopMarginFix, gs.paddingTop0)}>
                  <Col className={gs.paddingTop0} xs={12}>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                      <CircularProgressButton
                        variant='contained'
                        size='small'
                        color='secondary'
                        type='submit'
                        disabled={submitInProgress}
                        disableElevation
                        className={clsx(gs.marginTopSmall, gs.marginBottomSmall, gs.flex, gs.flexJustifyCenterImportant)}
                        text={t('common:registrationCourseButtonLabelRegister')}
                      />
                    </div>
                  </Col>
                </Row>
              </>
            }
            {
              coursePrefills.error
              && <>
                <Typography>
                  Das Abrufen der Formular-Daten ist ein Fehler aufgetreten. bitte versuchen sie es zu einem späteren zeitpunkt erneut.
                </Typography>
                <CircularProgressButton
                  variant='contained'
                  size='small'
                  color='secondary'
                  type='submit'
                  onClick={fetchRegisterCoursePrefills}
                  disabled={coursePrefills.inProgress}
                  disableElevation
                  className={clsx(gs.marginTopSmall, gs.marginBottomSmall)}
                  text={t('common:tryAgain')}
                />
              </>
            }
          </GridContainer>
        </form>
      </FormProvider>
    </FormContext.Provider>
  </ScreenContainerMain>
}

export default RegistrationCourse
